import type { StudyDate } from './time.js';
import type { StudentPath } from './flavour.js';

/**
 * THE EVENT LOG — the wire format.
 *
 * This is the one schema that ships onto user devices, so it is the most
 * expensive thing in the repo to change. Three rules hold it together:
 *
 *  1. Append-only. Nothing is ever mutated or removed. Edits and deletes are
 *     *new* events pointing at the original id (see the `*.corrected` and
 *     `*.deleted` types). A student who types 25 minutes instead of 2h 5m
 *     must be able to fix it, and "just update the row" is not available.
 *
 *  2. Nothing derived is stored. No XP totals, no streak counts, no rank.
 *     Those are folds over this log, which is what lets ruleset v2 replay
 *     history rather than migrate it.
 *
 *  3. Ids are ULIDs — time-sortable and collision-free across devices — so
 *     merging two logs is a union by id with no coordination.
 *
 * NOT in here, deliberately: timer ticks (see timer.ts), settings, and
 * reminder scheduling. See the notes on those files.
 */

export type EventId = string;
export type HabitId = string;
export type TaskId = string;
export type GoalId = string;
export type EntryId = string;
export type SessionId = string;
export type MeasureId = string;
export type ReadingId = string;

/** Habit repetition. Kept intentionally small — spec §4 warns against turning
 *  this into a project manager. */
export type Schedule =
  | { kind: 'daily' }
  /** 0 = Sunday, matching the Bangladeshi week start. */
  | { kind: 'weekly'; days: number[] }
  | { kind: 'timesPerWeek'; count: number };

/**
 * What the work was *for*.
 *
 * `learn` is first contact with material; `review` is going back over it. The
 * split is not cosmetic — it is what lets the review scheduler propose a date,
 * which is the highest-evidence feature in the study half of the app.
 */
export type EntryKind = 'learn' | 'review';

/**
 * How the entry got here — typed in afterwards, or produced by a timer that
 * was running at the time. One fact, two ways of recording it; the fact is
 * the entry, and this says which route it took.
 */
export type EntryMode = 'manual' | 'timed';

/** The timer's own style. Only meaningful when `mode` is `'timed'`. */
export type SessionMode = 'focus' | 'pomodoro';

type Base = {
  id: EventId;
  /** UTC milliseconds. */
  ts: number;
  /** Minutes east of UTC when the event happened (Dhaka = +360). Stored
   *  per-event so travel and DST can't retroactively rewrite history. */
  tzOffsetMinutes: number;
  /** Denormalised from (ts, tzOffsetMinutes) under the 4am rollover. Costs a
   *  few bytes and buys cheap indexing plus immunity to the rollover rule
   *  ever changing — past events keep the day they were actually filed under. */
  studyDate: StudyDate;
  /**
   * Which surface wrote this — `'pwa'`, a future focus app, an imported Build.
   *
   * Nothing reads it yet, and it is here anyway: one log is meant to carry a
   * whole goal across more than one app, and provenance is the field you
   * cannot add later. Adding it now costs a string; adding it in a year means
   * backfilling every event already on every device, and guessing.
   *
   * Optional so logs written before it existed stay valid — absent means the
   * original PWA, which is the only thing that has ever written to one.
   */
  source?: string;
};

// ── Definitions ───────────────────────────────────────────────────────────
// Entities are event-sourced too, rather than living in a mutable table. It
// is more code, but it keeps ONE log and therefore one sync path — and it is
// what makes Study Builds (spec §19) nearly free: a Build is just a bundle of
// creation events replayed with fresh ids.

export type HabitCreated = Base & {
  type: 'habit.created';
  habitId: HabitId;
  name: string;
  schedule: Schedule;
  icon?: string;
  /** The two-minute version. See `Habit.floor` in state.ts. */
  floor?: string;
  /** Which goal this habit serves. See `Habit.goalId` in state.ts. */
  goalId?: GoalId;
  /** The if-then plan. See `Habit.anchor` in state.ts. */
  anchor?: string;
  place?: string;
};
export type HabitRenamed = Base & { type: 'habit.renamed'; habitId: HabitId; name: string };
/** Sets or clears the floor. An empty string clears it. */
export type HabitFloorSet = Base & { type: 'habit.floorSet'; habitId: HabitId; floor: string };
/**
 * Sets or clears the if-then plan. Empty strings clear.
 *
 * Both slots travel together because they are one sentence — changing where
 * you study without saying when produces half a plan, and the UI that writes
 * this always has both fields on screen.
 */
export type HabitPlanSet = Base & {
  type: 'habit.planSet';
  habitId: HabitId;
  anchor: string;
  place: string;
};
export type HabitRescheduled = Base & {
  type: 'habit.rescheduled';
  habitId: HabitId;
  schedule: Schedule;
};
export type HabitArchived = Base & { type: 'habit.archived'; habitId: HabitId };
/**
 * Carries the WHOLE ordered list, not "moved X above Y".
 *
 * Two devices that each reorder offline produce deltas that cannot be
 * meaningfully combined, whereas two full lists merge by the same rule as
 * everything else here: the later ULID wins, and nobody ends up with a
 * duplicated or vanished habit.
 */
export type HabitReordered = Base & { type: 'habit.reordered'; habitIds: HabitId[] };

export type TaskCreated = Base & {
  type: 'task.created';
  taskId: TaskId;
  name: string;
  track?: string;
  dueDate?: StudyDate;
  estimateMinutes?: number;
};
export type TaskEdited = Base & {
  type: 'task.edited';
  taskId: TaskId;
  name?: string;
  track?: string;
  dueDate?: StudyDate;
};
export type TaskArchived = Base & { type: 'task.archived'; taskId: TaskId };

/** Broad shape of the goal, so the product is not only for students. Drives
 *  copy and preset lists; never scoring. */
export type GoalCategory =
  | 'academic'
  | 'career'
  | 'skill'
  | 'fitness'
  | 'business'
  | 'creative'
  | 'personal'
  | 'custom';

export type GoalStarted = Base & {
  type: 'goal.started';
  goalId: GoalId;
  preset: string;
  title: string;
  category: GoalCategory;
  targetDate: StudyDate;
  tracks: string[];
  /** All four optional, because onboarding must not block on thinking. The
   *  obstacle in particular is worth more after the first missed day than at
   *  minute one — see the note on `Goal` in state.ts. */
  outcome?: string;
  obstacle?: string;
  doneWhen?: string;
  parts?: string[];
};

/**
 * Any later change to the goal's text.
 *
 * One event rather than one per field: these are all rare, all corrections or
 * refinements of the same object, and a `goal.obstacleSet` alongside a
 * `goal.outcomeSet` would be four events that never appear apart. Absent
 * fields mean "unchanged", never "cleared".
 */
export type GoalEdited = Base & {
  type: 'goal.edited';
  goalId: GoalId;
  title?: string;
  outcome?: string;
  obstacle?: string;
  doneWhen?: string;
  targetDate?: StudyDate;
};

/** The ordered work. Replaces the list wholesale — parts get re-planned as a
 *  set ("I'm dropping the last three chapters"), not one at a time. */
export type GoalPartsSet = Base & {
  type: 'goal.partsSet';
  goalId: GoalId;
  parts: string[];
};

export type GoalPartCompleted = Base & {
  type: 'goal.partCompleted';
  goalId: GoalId;
  part: string;
};

export type GoalEnded = Base & {
  type: 'goal.ended';
  goalId: GoalId;
  status: 'completed' | 'abandoned';
};

// ── Activity ──────────────────────────────────────────────────────────────

/**
 * How much of the promise was kept.
 *
 * `full` is the habit as written. `floor` is its two-minute version — the
 * version that is still true on the worst day of the month. Both keep the
 * streak alive; `floor` scores a fraction (§48 #2).
 *
 * All-or-nothing completion manufactures ruined days, and a ruined day is the
 * documented start of the abandonment curve. This is the cheapest available
 * fix: one enum.
 */
export type CompletionLevel = 'floor' | 'full';

/**
 * `level` is optional because events written before the floor existed have no
 * level and must keep replaying identically. Absent means `full`.
 */
export type HabitCompleted = Base & {
  type: 'habit.completed';
  habitId: HabitId;
  level?: CompletionLevel;
};
/** Undo. Not a delete — the completion stays in the log, this supersedes it
 *  for the same (habitId, studyDate). */
export type HabitUncompleted = Base & { type: 'habit.uncompleted'; habitId: HabitId };

export type TaskCompleted = Base & { type: 'task.completed'; taskId: TaskId };
export type TaskUncompleted = Base & { type: 'task.uncompleted'; taskId: TaskId };

/**
 * ONE record of work done, however it was captured.
 *
 * An hour typed in by hand and an hour measured by the timer are the same
 * fact. Modelling them as two event types meant the timer's minutes landed in
 * the day's total while producing no entry anyone could see, list or correct —
 * so a student saw "2h 30m" above a list containing only the hour they had
 * typed, and a mistimed session could not be fixed at all.
 *
 * `mode` records the route. Everything else is the fact.
 *
 * `intention` / `nextStep` / `recall` are written by the focus slice: the
 * question asked on starting, the re-entry point written on stopping, and the
 * retrieval prompt answered at the end. They are optional and unused today —
 * declared here so the shape is decided once rather than twice.
 */
export type EntryLogged = Base & {
  type: 'entry.logged';
  entryId: EntryId;
  /** Subject, paper, skill — whatever the student is dividing their work by. */
  track: string;
  minutes: number;
  kind: EntryKind;
  mode: EntryMode;
  /** Timed entries only: the session this came out of, and what was planned. */
  sessionId?: SessionId;
  planMinutes?: number;
  timerMode?: SessionMode;
  intention?: string;
  nextStep?: string;
  recall?: string;
};
export type EntryCorrected = Base & {
  type: 'entry.corrected';
  entryId: EntryId;
  minutes: number;
  track?: string;
  kind?: EntryKind;
};
export type EntryDeleted = Base & { type: 'entry.deleted'; entryId: EntryId };

/** A timer that was given up on. Produces no entry and earns nothing — see
 *  the note in reduce.ts. The timer's tick state never reaches the log at
 *  all; see timer.ts. */
export type SessionAbandoned = Base & {
  type: 'session.abandoned';
  sessionId: SessionId;
  track: string;
  minutes: number;
};

/**
 * What a recorded number MEANS — and the only thing about it the app decides.
 *
 * Three shapes, each carrying its own direction, so nothing has to be
 * configured and there is no "manage your metrics" screen to get lost in:
 *
 *  • `outOf` — 62 out of 80. Higher is better, and different totals compare
 *    as percentages, which is what makes a 40/50 and a 78/100 one trend.
 *  • `rank`  — 12 of 340. LOWER is better. The coaching-centre merit position,
 *    which students here watch more closely than any score.
 *  • `count` — 45 pages, 7 hours, 62 kg. **No direction is asserted at all.**
 *    The app has no business deciding whether a number going down is good, so
 *    for anything that is not a result it records and does not judge.
 *
 * Only `outOf` and `rank`, and only when attached to a track, may be read as
 * evidence of skill (§53.8). A count never claims the student improved.
 */
export type MeasureShape = 'outOf' | 'rank' | 'count';

/**
 * A thing worth recording a number for, named by the student.
 *
 * Comes into existence the first time one is recorded — exactly how a track
 * does. There is no screen for creating these and there will not be one:
 * deciding what to track is system-design work, and system-design work is
 * what students do instead of studying (§45).
 */
export type MeasureDefined = Base & {
  type: 'measure.defined';
  measureId: MeasureId;
  name: string;
  shape: MeasureShape;
  /** Subject this measures, when it measures one. Required for a measure to
   *  count as evidence of skill. */
  track?: string;
  /** The denominator, for `outOf` and `rank`. */
  outOf?: number;
  /** For `count` — "pages", "kg", "hours". */
  unit?: string;
};

export type ReadingRecorded = Base & {
  type: 'reading.recorded';
  readingId: ReadingId;
  measureId: MeasureId;
  value: number;
  /**
   * The session that produced it, when there was one.
   *
   * This is the link the whole product is about: three hours of physics on
   * Tuesday, and 62/80 on Thursday. Without it, effort and outcome are two
   * lists that never meet.
   */
  entryId?: EntryId;
  /** Overrides the measure's denominator for this one reading — a mock out of
   *  100 in a series otherwise out of 50 is normal and must not need a second
   *  measure. */
  outOf?: number;
};
export type ReadingCorrected = Base & {
  type: 'reading.corrected';
  readingId: ReadingId;
  value: number;
  outOf?: number;
};
export type ReadingDeleted = Base & { type: 'reading.deleted'; readingId: ReadingId };

// ── Materialised facts ────────────────────────────────────────────────────
// Achievements are *detected* by folding, but once earned they are written
// into the log as facts. Spec §11 says they're permanent — and pure
// recomputation would silently revoke someone's 100-hour badge the moment the
// ruleset is rebalanced. Derive to detect; emit to remember.

export type AchievementUnlocked = Base & { type: 'achievement.unlocked'; achievementId: string };
/** Same reasoning, plus it gives share cards a dated history: "reached
 *  Scholar on 12 Aug". */
export type RankReached = Base & { type: 'rank.reached'; rankId: string; power: number };

export type ProfileNamed = Base & { type: 'profile.named'; name: string };

/**
 * What the student says they are preparing for.
 *
 * In the journal rather than in device-local settings, unlike the coach's
 * seen-flags: this is a fact ABOUT the student, it belongs in their export,
 * and it should follow them to a new phone. It is also the key their goal
 * presets are filed under, which makes it closer to data than to a setting.
 */
export type ProfilePathSet = Base & { type: 'profile.pathSet'; path: StudentPath };

export type AppEvent =
  | HabitCreated
  | HabitRenamed
  | HabitFloorSet
  | HabitPlanSet
  | HabitRescheduled
  | HabitArchived
  | HabitReordered
  | TaskCreated
  | TaskEdited
  | TaskArchived
  | GoalStarted
  | GoalEdited
  | GoalPartsSet
  | GoalPartCompleted
  | GoalEnded
  | HabitCompleted
  | HabitUncompleted
  | TaskCompleted
  | TaskUncompleted
  | EntryLogged
  | EntryCorrected
  | EntryDeleted
  | SessionAbandoned
  | MeasureDefined
  | ReadingRecorded
  | ReadingCorrected
  | ReadingDeleted
  | AchievementUnlocked
  | RankReached
  | ProfileNamed
  | ProfilePathSet;

export type AppEventType = AppEvent['type'];

/** ULIDs sort lexicographically by creation time, so this is the canonical
 *  log order on every device without any clock comparison. */
export function compareEvents(a: AppEvent, b: AppEvent): number {
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/**
 * Union by id.
 *
 * The entire payoff of the event-log decision: merging two devices' histories
 * needs no vector clocks, no last-write-wins, and no conflict UI. Duplicate
 * ids are the same event by construction.
 */
export function mergeEvents(a: readonly AppEvent[], b: readonly AppEvent[]): AppEvent[] {
  const byId = new Map<EventId, AppEvent>();
  for (const event of a) byId.set(event.id, event);
  for (const event of b) byId.set(event.id, event);
  return [...byId.values()].sort(compareEvents);
}
